import { makeAutoObservable, runInAction } from 'mobx'
import { handleAxiosError } from '~/shared/lib'
import type {
  Autopurchase,
  AutopurchaseFormType,
  AutopurchaseProductOption,
} from './types'
import { AutopurchasesService } from '~/shared/api/services/autopurchases'
import { ProductsService } from '~/shared/api/services/products'
import type { Product } from '~/shared/api/services/products/types'

const INITIAL_FORM_AUTOPURCHASE: AutopurchaseFormType = {
  manufacturerId: null,
  productVariationId: null,
  minCountInStock: null,
  demandsCount: null,
  productName: '',
}

export class AutopurchasesStore {
  autopurchases: Autopurchase[] = []

  products: Product[] = []

  productOptions: AutopurchaseProductOption[] = []

  selectedOptionValueIds: Record<string, string> = {}

  loaders = {
    autopurchases: false,
    loadMoreAutopurchases: false,
    createAutopurchase: false,
    deleteAutopurchase: false,
    products: false,
    productOptions: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  formAutopurchase: AutopurchaseFormType = INITIAL_FORM_AUTOPURCHASE

  filters: AutopurchaseFormType = INITIAL_FORM_AUTOPURCHASE

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof typeof this.loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof typeof this.loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormField = (
    key: keyof AutopurchaseFormType,
    value: string | number | null,
  ) => {
    this.formAutopurchase = { ...this.formAutopurchase, [key]: value }
  }

  clearForm = () => {
    this.formAutopurchase = INITIAL_FORM_AUTOPURCHASE
    this.productOptions = []
    this.selectedOptionValueIds = {}
  }

  resetProductOptions = () => {
    this.productOptions = []
    this.selectedOptionValueIds = {}
  }

  setSelectedOptionValue = (optionId: string, valueId: string) => {
    this.selectedOptionValueIds = {
      ...this.selectedOptionValueIds,
      [optionId]: valueId,
    }
  }

  setFilters = (filters: Partial<AutopurchaseFormType>) => {
    this.filters = {
      ...this.filters,
      ...filters,
    }

    this.requestSettings = {
      ...this.requestSettings,
      offset: 0,
      isAllLoaded: false,
    }
  }

  clearFilters = () => {
    this.filters = INITIAL_FORM_AUTOPURCHASE
  }

  get filteredAutopurchases() {
    return this.autopurchases
  }

  getProducts = async (manufacturerId?: string | null, q?: string) => {
    try {
      this.activeLoader('products')
      this.resetProductOptions()

      const params: Record<string, unknown> = {}

      if (manufacturerId) {
        params.manufacturer = [manufacturerId]
      }

      if (q?.trim()) {
        params.q = q.trim()
      }

      const { data } = await ProductsService.getProducts({ params })
      runInAction(() => {
        this.products = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('products')
    }
  }

  getAllProducts = async () => {
    try {
      this.activeLoader('products')
      this.resetProductOptions()

      const { data } = await ProductsService.getProducts({})
      runInAction(() => {
        this.products = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('products')
    }
  }

  getProductOptions = async (productId: string) => {
    try {
      this.activeLoader('productOptions')

      const response = await ProductsService.getProductOptions(productId)

      runInAction(() => {
        this.productOptions = response.data || []
        this.selectedOptionValueIds = {}
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('productOptions')
    }
  }

  createAutopurchase = async () => {
    this.activeLoader('createAutopurchase')

    try {
      const { productVariationId: currentProductVariationId } =
        this.formAutopurchase
      let productVariationId = currentProductVariationId
      const optionValues = Object.values(this.selectedOptionValueIds)

      if (productVariationId && optionValues.length) {
        const response = await ProductsService.getAvailableProductVariation(
          productVariationId,
          {
            params: {
              optionValues,
            },
          },
        )

        const {
          data: { id },
        } = response
        productVariationId = id
      }

      const { data } = await AutopurchasesService.createAutopurchase({
        productVariationId: productVariationId || null,
        minCountInStock:
          this.formAutopurchase.minCountInStock === ''
            ? null
            : this.formAutopurchase.minCountInStock,
        demandsCount:
          this.formAutopurchase.demandsCount === ''
            ? null
            : this.formAutopurchase.demandsCount,
      })

      runInAction(() => {
        this.autopurchases = [data, ...this.autopurchases]
        this.clearForm()
      })

      return data
    } finally {
      this.closeLoader('createAutopurchase')
    }
  }

  deleteAutopurchase = async (id: string) => {
    this.activeLoader('deleteAutopurchase')

    try {
      await AutopurchasesService.deleteAutopurchase(id)
      runInAction(() => {
        this.autopurchases = this.autopurchases.filter((a) => a.id !== id)
        this.requestSettings = {
          ...this.requestSettings,
          offset: 0,
          isAllLoaded: false,
        }
      })

      await this.getAutopurchases()
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('deleteAutopurchase')
    }
  }

  getAutopurchases = async (options?: { action?: 'loadMoreAutopurchases' }) => {
    const { action } = options || {}

    if (
      action === 'loadMoreAutopurchases' &&
      this.requestSettings.isAllLoaded
    ) {
      return
    }

    this.activeLoader(action || 'autopurchases')

    try {
      const params: Record<string, unknown> = {
        limit: this.requestSettings.limit,
        offset:
          action !== 'loadMoreAutopurchases' ? 0 : this.requestSettings.offset,
      }

      if (this.filters.manufacturerId) {
        params.manufacturerId = [this.filters.manufacturerId]
      }
      if (this.filters.productName) {
        params.productName = this.filters.productName
      }
      if (
        this.filters.minCountInStock !== null &&
        this.filters.minCountInStock !== undefined &&
        this.filters.minCountInStock !== ''
      ) {
        params.minCountInStock = this.filters.minCountInStock
      }
      if (
        this.filters.demandsCount !== null &&
        this.filters.demandsCount !== undefined &&
        this.filters.demandsCount !== ''
      ) {
        params.demandsCount = this.filters.demandsCount
      }

      const { data } = await AutopurchasesService.getAutopurchases({ params })

      if (action === 'loadMoreAutopurchases') {
        runInAction(() => {
          this.autopurchases = [...this.autopurchases, ...data]
        })
      } else {
        runInAction(() => {
          this.autopurchases = data
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreAutopurchases'
              ? this.requestSettings.offset + this.requestSettings.limit
              : this.requestSettings.limit,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'autopurchases')
    }
  }
}

export const autopurchasesStore = new AutopurchasesStore()
